import React, { useState } from 'react';
import { useMeta } from '../../model/meta';
import { usePaginatedResponse, useData } from '../../model/data';
import { getPicklists } from '../../model/picklist';
import { Container } from '../../components/Container/Container';
import { Card } from '../../components/Card/Card';
import { SortableList } from '../../components/SortableList/SortableList';
import { picklistPath } from '../../routes';
import { formatDateTime } from '../../util/util';
import { Button } from '../../components/Button/Button';
import { handleError } from '../../model/notification';
import { useQueryAsState } from '../../model/history';
import { Pagination } from '../../components/Pagination/Pagination';
import { t } from '../../locales/translate';
import { compact } from 'lodash';
import { SortByIcon } from '../../components/SortableList/SortByIcon';

export function Picklists(): JSX.Element {
  // HOOKS
  useMeta({ title: t.model.picklist._plural });
  const [params] = useQueryAsState();
  const paginator = usePaginatedResponse(
    (page, perPage) =>
      getPicklists({
        page,
        perPage,
        sortBy: params.sortBy,
        sortDir: params.sortDir,
      }),
    JSON.stringify(params)
  );
  const picklists = useData((db) => compact(paginator.currentIds.map((e) => db.table('picklists').find(e))));

  const [reloadLoading, setReloadLoading] = useState(false);

  async function reloadPicklists(): Promise<void> {
    setReloadLoading(true);
    try {
      await getPicklists({ page: 0, perPage: 10 });
    } catch (e) {
      handleError(e);
    }
    setReloadLoading(false);
  }

  return (
    <Container>
      <Card title={t.model.picklist._plural} elevation={2} fullbleed pagination={<Pagination {...paginator} />}>
        <SortableList
          loading={paginator.loading || reloadLoading}
          error={paginator.error}
          items={picklists}
          rowLink={(row) => picklistPath(row)}
          columns={[
            {
              label: 'id',
              dataKey: 'reference',
            },
            {
              label: t.model.delivery._plural,
              dataKey: 'deliveryIds',
              cellRenderer: (row) => (row.deliveryIds ? row.deliveryIds.length : 0),
            },
            {
              label: t.model.datetime.createdAt,
              dataKey: 'createdAt',
              cellRenderer: (row) => formatDateTime(row.createdAt),
              apiSortIcon: <SortByIcon sortBy="created_at" />,
            },
            {
              label: 'PDF',
              dataKey: 'pdfUrl',
              disableLink: true,
              cellRenderer: (row) => {
                if (row.pdfUrl) {
                  return (
                    <a className="button button--small" href={row.pdfUrl} target="_blank" rel="noopener noreferrer">
                      {t.actions.download}
                    </a>
                  );
                }
                return <Button title={t.actions.reload} size="small" onClick={reloadPicklists} />;
              },
            },
            {
              label: t.model.picklist.addresslistCSV,
              dataKey: 'addressesCsvDownloadUrl',
              disableLink: true,
              cellRenderer: (row) => {
                if (row.addressesCsvDownloadUrl) {
                  return (
                    <a
                      className="button button--small"
                      href={row.addressesCsvDownloadUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t.actions.download}
                    </a>
                  );
                }
                return <Button title={t.actions.reload} size="small" onClick={reloadPicklists} />;
              },
            },
            {
              label: t.model.picklist.ticketlistPDF,
              dataKey: 'ticketlistPdfUrl',
              disableLink: true,
              cellRenderer: (row) => {
                if (row.ticketlistPdfUrl) {
                  return (
                    <a
                      className="button button--small"
                      href={row.ticketlistPdfUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {t.actions.download}
                    </a>
                  );
                }
                return null;
              },
            },
          ]}
        />
      </Card>
    </Container>
  );
}
